import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useTranslation } from 'react-i18next'; 
import { useSidebar } from './SidebarContext'; // 🎯 استدعاء حالة الانكماش
import axios from 'axios';
import { 
  ChevronDown, ChevronRight, ChevronLeft, HardDrive, Wrench, ShoppingCart, ListTree, MousePointer2, ClipboardList, Ticket, BookCopy, Package, Touchpad, Hammer, 
  BarChart, Settings, ShieldAlert, Construction, Search, PieChart, Warehouse, Users, FileText, Activity, User, Zap, BadgeAlert, Gauge, Map, Forklift,
  Cog, HardHat, AlertTriangle, Tags, Kanban, FileBox, FileBadge, FileCheck, Building2, UserCheck, FileStack, FileUp, FileCode2, Book, FilePlus, Shuffle,
  Clock, Menu, PanelLeftClose, PanelLeftOpen, CalendarClock  // 🎯 أيقونات الانكماش
} from 'lucide-react';

const NavGroup = ({ icon: Icon, label, children, isOpen, toggle, isRTL, isCollapsed }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [menuTop, setMenuTop] = useState(0);
  const timeoutRef = useRef(null); // 🎯 1. مرجع المؤقت السحري

  // 🎯 2. دالة الدخول (بتلغي أي أمر إخفاء سابق وتظهر القائمة)
  const handleMouseEnter = (e) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (isCollapsed) {
      const rect = e.currentTarget.getBoundingClientRect();
      setMenuTop(rect.top); 
      setShowTooltip(true);
    }
  };

  // 🎯 3. دالة الخروج (بتدي سماحية 250 ملي ثانية قبل ما تخفي القائمة)
  const handleMouseLeave = () => {
    if (isCollapsed) {
      timeoutRef.current = setTimeout(() => {
        setShowTooltip(false);
      }, 250); 
    }
  };

  return (
    <div 
      className="mb-1 relative"
      onMouseEnter={handleMouseEnter} // 🎯 ربطنا الدالة الجديدة
      onMouseLeave={handleMouseLeave} // 🎯 ربطنا الدالة الجديدة
    >
      <button 
        onClick={() => !isCollapsed && toggle()} 
        className={`w-full flex items-center ${isCollapsed ? 'justify-center py-3.5 my-1 mx-auto w-12 rounded-xl' : 'justify-between px-4 py-3'} text-[12.5px] font-black transition-all duration-300 ${
          isOpen && !isCollapsed
            ? 'bg-gradient-to-r from-[#005495] to-[#00a1df] text-white shadow-md' 
            : isCollapsed
              ? 'text-[#005495] hover:bg-blue-50 hover:text-[#00a1df] hover:shadow-sm'
              : 'text-slate-600 hover:bg-slate-50 hover:text-[#005495]'
        } ${isCollapsed && showTooltip ? 'bg-blue-50 text-[#00a1df] shadow-sm ring-1 ring-blue-100' : ''}`}
      >
        <div className="flex items-center gap-3">
          <Icon 
            size={isCollapsed ? 22 : 17} 
            className={`transition-all duration-300 ${isOpen && !isCollapsed ? 'text-white' : ''} ${isCollapsed && showTooltip ? 'scale-110' : ''}`} 
          />
          {!isCollapsed && <span className="tracking-tight uppercase">{label}</span>}
        </div>
        {!isCollapsed && (isOpen ? <ChevronDown size={14} /> : (isRTL ? <ChevronLeft size={14} /> : <ChevronRight size={14} />))} 
      </button>
      
      {!isCollapsed && isOpen && (
        <div className="bg-white py-1 animate-in fade-in slide-in-from-top-1 duration-200">
          {children}
        </div>
      )}

      {isCollapsed && showTooltip && (
        <div 
          style={{ top: `${menuTop}px` }} 
          className={`fixed ${isRTL ? 'right-[64px] mr-2' : 'left-[64px] ml-2'} w-56 bg-white border border-slate-100 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.15)] rounded-xl py-2 z-[100] animate-in fade-in zoom-in-95`}
        >
          {/* 🎯 إضافة جسر شفاف (Transparent Bridge) عشان الماوس ما يقعش في الفراغ أبدأ */}
          <div className={`absolute top-0 h-full w-4 ${isRTL ? '-right-4' : '-left-4'} bg-transparent`} />

          <div className={`px-4 py-2 mb-1 border-b border-slate-50 font-black text-[#005495] uppercase text-[11px] tracking-widest bg-slate-50/50 ${isRTL ? 'text-right' : 'text-left'}`}>
            {label}
          </div>
          <div className="flex flex-col gap-0.5 px-1">
            {children}
          </div>
        </div>
      )}
    </div>
  );
};

const NavItem = ({ icon: Icon, label, onClick, badge, isRTL, isCollapsed }) => (
  <div 
    onClick={onClick}
    // 🎯 تظبيط المسافات (Padding) عشان تكون متناسقة جوه الـ Tooltip وجوه القائمة العادية
    className={`relative ${
      isCollapsed 
        ? 'mx-1 px-3 py-2.5 rounded-lg' // تصميم العنصر جوه القائمة الطائرة
        : (isRTL ? 'ml-2 mr-2 pr-10 pl-3 py-2' : 'ml-2 mr-2 pl-10 pr-3 py-2') // تصميم العنصر في القائمة العادية
    } text-[11.5px] text-slate-500 hover:text-white cursor-pointer rounded-lg transition-all duration-300 group overflow-hidden z-10`}
  >
    {/* 🎯 خلفية الهوفر المتدرجة */}
    <div className="absolute inset-0 w-0 bg-gradient-to-r from-[#005495] via-[#00a1df] to-[#4ade80] transition-all duration-500 ease-in-out group-hover:w-full" style={{ zIndex: -1 }}></div>
    <div className="flex items-center justify-between relative z-20">
      <div className="flex items-center gap-3">
        {Icon && <Icon size={isCollapsed ? 15 : 16} className="text-slate-400 group-hover:text-white transition-colors duration-300" />}
        <span className={`font-bold tracking-wide transition-transform duration-300 ${isRTL ? 'group-hover:-translate-x-1' : 'group-hover:translate-x-1'}`}>{label}</span>
      </div>
      {badge && <div className={`${isRTL ? 'mr-2' : 'ml-2'} relative z-10`}>{badge}</div>}
    </div>
  </div>
);

export default function Sidebar() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { isCollapsed, toggleSidebar } = useSidebar(); // 🎯 سحب حالة الانكماش
  
  const session = JSON.parse(localStorage.getItem('user') || '{}');
  const [searchTerm, setSearchTerm] = useState('');
  const [pendingCount, setPendingCount] = useState(0);

  const [openGroup, setOpenGroup] = useState(() => localStorage.getItem('sidebarOpenGroup') || 'Assets');
  const [recentPaths, setRecentPaths] = useState(() => JSON.parse(localStorage.getItem('recentPaths') || '[]'));

  const handleToggleGroup = (groupId) => {
    if (isCollapsed) return; // منع تغيير الحالة لو السايد بار منكمش
    const newGroup = openGroup === groupId ? null : groupId;
    setOpenGroup(newGroup);
    if (newGroup) {
      localStorage.setItem('sidebarOpenGroup', newGroup);
    } else {
      localStorage.removeItem('sidebarOpenGroup');
    }
  };

  const handleItemClick = (item) => {
    if (item.path !== 'external' && item.path !== '#') {
      setRecentPaths(prev => {
        const newPaths = [item.path, ...prev.filter(p => p !== item.path)].slice(0, 5);
        localStorage.setItem('recentPaths', JSON.stringify(newPaths));
        return newPaths;
      });
      navigate(item.path);
    } else if (item.path === 'external') {
      window.open(item.url, "_blank");
    }
  };

  const menuConfig = useMemo(() => [
    // ... (تكوين القائمة كما هو في الكود الأصلي لديك) ...
    {
      id: 'Settings',
      label: t('menu.settings'),
      icon: Settings,
      items: [
        { icon: Building2, label: t('menu.organization'), path: '/organizations', perm: ['organizations', 'read'] },
        { icon: MousePointer2, label: t('menu.domains'), path: '/domains', perm: ['domains', 'read'] },
        { label: t('menu.field_config'), path: '/admin/field-config', perm: ['field-config', 'read'] },
        { icon: ListTree, label: t('menu.classifications'), path: '/classifications', perm: ['classifications', 'read'] },
        { icon: FileUp, label: t('menu.importer'), path: '/uniimporter', perm: ['uniimporter', 'read'] },
        { icon: FileStack, label: t('menu.cobie'), path: '/cobiemanager', perm: ['cobiemanager', 'read'] },
        { icon: Zap, label: t('menu.workflow'), path: '/workflow', perm: ['workflow', 'read'] },
        { icon: FileCode2, label: t('menu.scripts'), path: '/admin/workflow/studio', perm: ['scripts', 'read'] },
      ]
    },
    {
      id: 'BI',
      label: t('menu.analytics'),
      icon: PieChart,
      items: [
        { icon: PieChart, label: t('menu.dashboards'), path: 'external', perm: ['kpi', 'read'], url: "https://superset.maqaree.com/" },
        { icon: Book, label: t('menu.report_designer'), path: '/reportlist', perm: ['reportlist', 'read'] },
        { icon: FilePlus, label: t('menu.report_wizard'), path: '/reportwizard', perm: ['reportwizard', 'read'] },
      ]
    },
    {
      id: 'Assets',
      label: t('menu.assets'),
      icon: HardDrive,
      items: [
        { icon: Forklift, label: t('menu.asset_registry'), path: '/assets', perm: ['assets', 'read'] },
        { icon: Map, label: t('menu.locations'), path: '/locations', perm: ['locations', 'read'] },
        { icon: BookCopy, label: t('menu.categories'), path: '/asset-categories', perm: ['asset-categories', 'read'] },
        { icon: Gauge, label: t('menu.meters'), path: '/meters', perm: ['meters', 'read'] },
        { icon: BadgeAlert, label: t('menu.failure_codes'), path: '/failure-codes', perm: ['failure-codes', 'read'] },
      ]
    },
    {
      id: 'WO',
      label: t('menu.work_management'),
      icon: Wrench,
      items: [
        { icon: Wrench, label: t('menu.wo_tracking'), path: '/workorders', perm: ['workorders', 'read'] },
        { icon: CalendarClock , label: t('menu.labor_reporting'), path: '/smartscheduler', perm: ['workorders', 'read'] },
      ]
    },
    {
      id: 'Resources',
      label: t('menu.resources'),
      icon: Construction,
      items: [
        { icon: Hammer, label: t('menu.crafts'), path: '/crafts', perm: ['crafts', 'read'] },
        { icon: Users, label: t('menu.labor'), path: '/labors', perm: ['labors', 'read'] },
        { icon: User, label: t('menu.person'), path: '/persons', perm: ['persons', 'read'] },
      ]
    },
    {
      id: 'SR',
      label: t('menu.service_desk'),
      icon: FileText,
      items: [
        { icon: ClipboardList, label: t('menu.service_requests'), path: '/sr', perm: ['sr', 'read'] },
        { icon: Ticket, label: t('menu.incidents'), path: '/incidents', perm: ['incidents', 'read'] },
      ]
    },
    {
      id: 'Purchasing',
      label: t('menu.procurement'),
      icon: ShoppingCart,
      items: [
        { icon: FileBox, label: t('menu.po'), path: '/po', perm: ['po', 'read'] },
        { icon: FileBadge, label: t('menu.pr'), path: '/pr', perm: ['pr', 'read'] },
        { icon: FileBadge, label: t('menu.rfq'), path: '/rfq', perm: ['rfq', 'read'] },
        { icon: Package, label: t('menu.receiving'), path: '/receiving', perm: ['receiving', 'read'], isReceiving: true },
        { icon: UserCheck, label: t('menu.vendors'), path: '/vendors', perm: ['vendors', 'read'] },
      ]
    },
    {
      id: 'CONTRACT',
      label: t('menu.contracts'),
      icon: FileCheck,
      items: [
        { icon: FileCheck, label: t('menu.contracts_master'), path: '/contracts', perm: ['contracts', 'read'] },
        { icon: FileCheck, label: t('menu.sla'), path: '/sla', perm: ['sla', 'read'] },
      ]
    },
    {
      id: 'Inventory',
      label: t('menu.inventory'),
      icon: Warehouse,
      items: [
        { icon: Touchpad, label: t('menu.item_master'), path: '/items', perm: ['items', 'read'] },
        { icon: Hammer, label: t('menu.tools'), path: '/tools', perm: ['tools', 'read'] },
        { icon: Package, label: t('menu.balances'), path: '/inventory', perm: ['inventory', 'read'] },
        { icon: Shuffle, label: t('menu.transfers'), path: '/inventoryusage', perm: ['inventoryusage', 'read'] },
        { icon: FileText, label: t('apps.MR'), path: '/mr', perm: ['mr', 'read'] },  
        { icon: Warehouse, label: t('menu.storerooms'), path: '/storerooms', perm: ['storerooms', 'read'] },
      ]
    },
    {
      id: 'PM',
      label: t('menu.planning'),
      icon: BarChart,
      items: [
        { icon: Cog, label: t('menu.pm_schedules'), path: '/pm', perm: ['pm', 'read'] },
        { icon: HardHat, label: t('menu.safety_plans'), path: '/safety', perm: ['safety', 'read'] },
        { icon: AlertTriangle, label: t('menu.hazards'), path: '/hazards', perm: ['hazards', 'read'] },
        { label: t('menu.precautions'), path: '/precautions', perm: ['precautions', 'read'] },
        { icon: Tags, label: t('menu.tagouts'), path: '/tagouts', perm: ['tagouts', 'read'] },
        { icon: Kanban, label: t('menu.job_plans'), path: '/jp', perm: ['jp', 'read'] },
      ]
    },
    {
      id: 'Security',
      label: t('menu.security'),
      icon: ShieldAlert,
      items: [
        { icon: User, label: t('menu.user_list'), path: '/users', perm: ['users', 'read'] },
        { icon: Users, label: t('menu.groups'), path: '/roles', perm: ['roles', 'read'] },
      ]
    }
  ], [t]); 

  const filteredMenu = useMemo(() => {
    return menuConfig.map(group => {
      const filteredItems = group.items.filter(item => {
        const matchesSearch = item.label.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesPermission = hasPermission(item.perm[0], item.perm[1]);
        return matchesSearch && matchesPermission;
      });

      if (group.label.toLowerCase().includes(searchTerm.toLowerCase())) {
        return { ...group, items: group.items.filter(i => hasPermission(i.perm[0], i.perm[1])) };
      }

      return filteredItems.length > 0 ? { ...group, items: filteredItems } : null;
    }).filter(group => group !== null);
  }, [searchTerm, menuConfig, hasPermission]);

  const finalMenu = useMemo(() => {
    let menu = [...filteredMenu];
    
    if (recentPaths.length > 0 && !searchTerm) {
      const allItems = menuConfig.flatMap(g => g.items);
      const recentItemsData = recentPaths
        .map(path => allItems.find(i => i.path === path))
        .filter(item => item && hasPermission(item.perm[0], item.perm[1]));

      if (recentItemsData.length > 0) {
        menu.unshift({
          id: 'Recent',
          label: t('menu.recent') || 'Recently Visited',
          icon: Clock,
          items: recentItemsData
        });
      }
    }
    return menu;
  }, [filteredMenu, recentPaths, menuConfig, searchTerm, hasPermission, t]);

  useEffect(() => {
    if (searchTerm && finalMenu.length > 0 && !isCollapsed) {
      setOpenGroup(finalMenu[0].id);
    }
  }, [searchTerm, finalMenu, isCollapsed]);

  const getPendingCount = useCallback(async () => {
    if (!session.org_id) return; 
    try {
      const res = await axios.get(`/api/po/pending-receipts/${session.org_id}`);
      setPendingCount(res.data.length); 
    } catch (err) { console.error(err); }
  }, [session.org_id]);
  
  useEffect(() => {
    getPendingCount();
    const interval = setInterval(getPendingCount, 120000); 
    return () => clearInterval(interval);
  }, [getPendingCount]);
  
 return (
    <aside className={`${isCollapsed ? 'w-16' : 'w-64'} bg-white flex flex-col h-full shadow-[5px_0px_15px_rgba(0,0,0,0.05)] overflow-visible border-slate-200 transition-all duration-300 shrink-0 z-40 relative ${isRTL ? 'border-l' : 'border-r'}`}>
      
      {/* 🎯 شريط الانكماش السحري (Maximo Style) - بطول الشاشة */}
      <div
        onClick={toggleSidebar}
        className={`absolute top-0 bottom-0 ${isRTL ? 'left-0' : 'right-0'} w-3 hover:w-5 cursor-pointer z-50 flex items-center justify-center group transition-all duration-300`}
        title={isCollapsed ? t('sidebar.expand', 'Expand') : t('sidebar.collapse', 'Collapse')}
      >
        {/* الخط الطولي اللي بينور في الخلفية */}
        <div className={`absolute ${isRTL ? 'left-0' : 'right-0'} h-full w-[1px] bg-slate-200 group-hover:bg-[#00a1df] group-hover:w-full transition-all duration-300`}></div>
        
        {/* المقبض (Grip) في المنتصف */}
        <div className="relative z-10 flex items-center justify-center w-full h-12 bg-white border border-slate-200 group-hover:bg-[#00a1df] group-hover:border-[#00a1df] rounded-sm shadow-sm text-slate-400 group-hover:text-white transition-all duration-300">
          {isRTL ? (
            isCollapsed ? <ChevronLeft size={14} strokeWidth={3} /> : <ChevronRight size={14} strokeWidth={3} />
          ) : (
            isCollapsed ? <ChevronRight size={14} strokeWidth={3} /> : <ChevronLeft size={14} strokeWidth={3} />
          )}
        </div>
      </div>

      <div className={`p-4 bg-gradient-to-br from-[#005495] to-[#00a1df] flex flex-col items-center justify-center relative overflow-hidden shrink-0 text-center uppercase transition-all duration-300 ${isCollapsed ? 'py-4 px-2' : ''}`}>
        {!isCollapsed ? (
          <>
            <h1 className="text-white font-black text-sm tracking-tighter z-10">{t('brand.name')}</h1>
            <p className="text-[9px] text-blue-100 font-bold uppercase tracking-[0.3em] z-10">{t('brand.tagline')}</p>
          </>
        ) : (
          <h1 className="text-white font-black text-sm tracking-tighter z-10 truncate w-full" title={t('brand.name')}>TEK</h1>
        )}
      </div>

      {!isCollapsed && (
        <div className="p-3 bg-white sticky top-0 border-b border-slate-100 z-10">
          <div className="relative group">
            <input 
              type="text" 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('search.placeholder')} 
              className={`w-full px-3 py-1.5 ${isRTL ? 'pr-8' : 'pl-8'} text-[11px] bg-slate-50 border border-slate-200 rounded-lg outline-none focus:border-[#00a1df] transition-all font-bold text-slate-600`} 
            />
            <Search size={13} className={`absolute ${isRTL ? 'right-2.5' : 'left-2.5'} top-2.5 text-slate-400 group-focus-within:text-[#005495]`} />
          </div>
        </div>
      )}

      <nav className="flex-1 overflow-y-auto overflow-x-visible scrollbar-hide py-2">
        {finalMenu.map(group => (
          <NavGroup 
            key={group.id}
            icon={group.icon} 
            label={group.label} 
            isOpen={openGroup === group.id} 
            toggle={() => handleToggleGroup(group.id)}
            isRTL={isRTL} 
            isCollapsed={isCollapsed}
          >
            {group.items.map((item, idx) => (
              <NavItem 
                key={`${group.id}-${idx}`}
                icon={item.icon}
                label={item.label} 
                isRTL={isRTL} 
                isCollapsed={isCollapsed}
                onClick={() => handleItemClick(item)} 
                badge={item.isReceiving && pendingCount > 0 ? (
                  <span className="bg-[#00a1df] text-white text-[9px] font-black px-2 py-0.5 rounded-full shadow-md animate-pulse">
                    {pendingCount}
                  </span>
                ) : null}
              />
            ))}
          </NavGroup>
        ))}
      </nav>

      {!isCollapsed && (
        <div className="p-3 bg-slate-50 border-t border-slate-200 flex items-center justify-center shrink-0">
          <span className="text-[10px] text-slate-400 font-bold uppercase tracking-[0.2em]">{t('brand.version')}</span>
        </div>
      )}
    </aside>
  );
}